'use client';

import { useState, useCallback } from 'react';
import { useCalendarStore } from '@/stores/calendar-store';
import { uiFb } from '@/lib/i18n';
import { LinkIcon, CheckIcon, WhatsAppIcon, FacebookIcon, XIcon } from './share-icons';
import { getShareUrl } from '@/lib/referral-url';

/* ── Shared button style ─────────────────────────────────────────── */

export const BTN =
  'rounded-md p-2 min-h-[32px] min-w-[32px] flex items-center justify-center text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors';

/* ── Types ────────────────────────────────────────────────────────── */

interface SocialShareButtonsProps {
  title: string;
  subtitle?: string;
  url?: string;
  /** Add role="menuitem" when inside a popover menu */
  inMenu?: boolean;
}

/* ── Component ───────────────────────────────────────────────────── */

export function SocialShareButtons({
  title,
  subtitle,
  url,
  inMenu,
}: SocialShareButtonsProps) {
  const lang = useCalendarStore((s) => s.lang);
  const [copied, setCopied] = useState(false);

  const shareUrl = getShareUrl(url);
  const roleProps = inMenu ? { role: 'menuitem' as const } : {};

  /* ── Actions ─────────────────────────────────────────────────── */

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      window._paq?.push(['trackEvent', 'Share', 'CopyLink', title]);
    } catch {
      // Fallback for older browsers
    }
  }, [shareUrl, title]);

  const handleWhatsApp = useCallback(() => {
    const waText = uiFb('ui.share_wa_text', '{title} 📅\nCheck the dates on vacant.day: {url}', lang, { title, url: shareUrl });
    const waUrl = `https://wa.me/?text=${encodeURIComponent(waText)}`;
    window.open(waUrl, '_blank');
    window._paq?.push(['trackEvent', 'Share', 'WhatsApp', title]);
  }, [shareUrl, title, lang]);

  const handleFacebook = useCallback(() => {
    const quote = uiFb('ui.share_fb_quote', 'Useful reference for planning around {title}. Full calendar at vacant.day.', lang, { title });
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(quote)}`;
    window.open(fbUrl, '_blank');
    window._paq?.push(['trackEvent', 'Share', 'Facebook', title]);
  }, [shareUrl, title, lang]);

  const handleTwitter = useCallback(() => {
    const xText = uiFb('ui.share_x_text', '{title} — dates and details on vacant.day', lang, { title });
    const tUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(xText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(tUrl, '_blank');
    window._paq?.push(['trackEvent', 'Share', 'Twitter', title]);
  }, [shareUrl, title, lang]);

  /* ── Labels ──────────────────────────────────────────────────── */

  const labelCopy = uiFb('ui.copy_link', 'Copy link', lang);
  const labelWhatsApp = uiFb('ui.share_whatsapp', 'Share on WhatsApp', lang);
  const labelFacebook = uiFb('ui.share_facebook', 'Share on Facebook', lang);
  const labelX = uiFb('ui.share_x', 'Share on X', lang);
  const labelCopied = uiFb('ui.copied', 'Copied!', lang);

  return (
    <>
      <button
        onClick={handleCopyLink}
        className={BTN}
        aria-label={copied ? labelCopied : labelCopy}
        title={copied ? labelCopied : labelCopy}
        {...roleProps}
      >
        {copied ? <CheckIcon /> : <LinkIcon />}
      </button>
      <button
        onClick={handleWhatsApp}
        className={BTN}
        aria-label={labelWhatsApp}
        title={labelWhatsApp}
        {...roleProps}
      >
        <WhatsAppIcon />
      </button>
      <button
        onClick={handleFacebook}
        className={BTN}
        aria-label={labelFacebook}
        title={labelFacebook}
        {...roleProps}
      >
        <FacebookIcon />
      </button>
      <button
        onClick={handleTwitter}
        className={BTN}
        aria-label={labelX}
        title={labelX}
        {...roleProps}
      >
        <XIcon />
      </button>
    </>
  );
}
